import React from 'react';
import { Button, Alert, Spinner } from 'react-bootstrap';
import { Calendar, AlertTriangle, Lock, Unlock, Clock, CreditCard } from 'lucide-react';

const VatModernHeader = ({
    vatPeriod,
    totalVat,
    undoingClose,
    handleUndoClose,
    handleVatPeriodClose,
    loading,
    currentStep
}) => {
    // Prefer stored vat_total (set during XML generation) over live totalVat
    // After the zeroing verification, live totalVat is zero but vat_total has the correct declared amount
    const displayAmount = vatPeriod?.vat_total || totalVat || 0;

    const formatCurrency = (amount) => {
        const numAmount = Number(amount) || 0;
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(numAmount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('sv-SE');
    };

    const isOverdue = () => {
        if (!vatPeriod || vatPeriod.state !== 'open') return false;
        return new Date(vatPeriod.due_date) < new Date();
    };

    const isClosed = vatPeriod?.state === 'closed';

    return (
        <>
            <style>{`
                .vat-header {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1.25rem 1.5rem;
                    margin-bottom: 1.5rem;
                    color: #1e293b;
                }
                .vat-title {
                    font-size: 1.35rem;
                    font-weight: 600;
                    margin-bottom: 0.75rem;
                    display: flex;
                    align-items: center;
                    gap: 0.625rem;
                    color: #1e293b;
                }
                .vat-badges {
                    display: flex;
                    gap: 0.5rem;
                    align-items: center;
                    flex-wrap: wrap;
                }
                .vat-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.375rem;
                    padding: 0.3rem 0.625rem;
                    border-radius: 16px;
                    font-size: 0.75rem;
                    font-weight: 500;
                    white-space: nowrap;
                }
                .vat-badge-open { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
                .vat-badge-closed { background: #fee2e2; color: #dc2626; border: 1px solid #fecaca; }
                .vat-badge-due { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
                .vat-badge-overdue { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
                .vat-badge-amount-pay { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
                .vat-badge-amount-refund { background: #f0fdf4; color: #059669; border: 1px solid #bbf7d0; }
                .vat-badge-neutral { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
                @media (max-width: 768px) {
                    .vat-header { padding: 1rem; }
                    .vat-title { font-size: 1.1rem; }
                }
            `}</style>

            <div className="vat-header">
                <div className="d-flex justify-content-between align-items-start">
                    <h1 className="vat-title">
                        <Calendar size={22} color="#3b82f6" />
                        Momsrapport {vatPeriod && formatDate(vatPeriod.start_date)} — {vatPeriod && formatDate(vatPeriod.end_date)}
                    </h1>
                    {!isClosed && handleVatPeriodClose && currentStep === 'instructions' && (
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleVatPeriodClose}
                            disabled={loading || undoingClose}
                            style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}
                        >
                            Stäng period (redan bokförd)
                        </Button>
                    )}
                </div>

                <div className="vat-badges">
                    {/* Status */}
                    <span className={`vat-badge ${isClosed ? 'vat-badge-closed' : 'vat-badge-open'}`}>
                        {isClosed ? <Lock size={12} /> : <Unlock size={12} />}
                        {isClosed ? 'Stängd' : 'Öppen'}
                    </span>

                    {/* Due date */}
                    <span className={`vat-badge ${isOverdue() ? 'vat-badge-overdue' : 'vat-badge-due'}`}>
                        {isOverdue() ? <AlertTriangle size={12} /> : <Clock size={12} />}
                        Deklarera & betala senast {vatPeriod && formatDate(vatPeriod.due_date)}
                        {isOverdue() && ' — försenad!'}
                    </span>

                    {/* Amount */}
                    <span className={`vat-badge ${displayAmount > 0 ? 'vat-badge-amount-pay' : displayAmount < 0 ? 'vat-badge-amount-refund' : 'vat-badge-neutral'}`}>
                        <CreditCard size={12} />
                        {displayAmount > 0 ? 'Att betala: ' : displayAmount < 0 ? 'Att få tillbaka: ' : ''}
                        {formatCurrency(Math.abs(displayAmount))}
                    </span>

                    {/* Period type */}
                    <span className="vat-badge vat-badge-neutral">
                        {vatPeriod?.period_type === 'monthly' ? 'Månadsvis' : vatPeriod?.period_type === 'yearly' ? 'Årsvis' : 'Kvartalsvis'}
                    </span>
                </div>

                {isClosed && (
                    <Alert variant="warning" className="d-flex align-items-center mt-3 mb-0">
                        <AlertTriangle size={18} className="me-2" />
                        <div className="flex-grow-1">
                            Denna momsperiod är stängd.
                            <Button variant="link" onClick={handleUndoClose} disabled={undoingClose} className="p-0 ms-2">
                                Ångra stängning
                            </Button>
                            {undoingClose && <Spinner animation="border" size="sm" className="ms-2" />}
                        </div>
                    </Alert>
                )}
            </div>
        </>
    );
};

export default VatModernHeader;
